import { CascadiaStandardIcon } from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import { createRef, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { FileInputInfo } from "./UploadInfo";
import { useMediaQuery } from "../../components/utils";
import { Icon } from "../../foundations/icons/Icon/Icon";
import { Text } from "../../foundations/typography/Text";

export interface UploadProps {
  onFileSelected(files: File | FileList): void;
  multiple?: boolean;
  icon?: CascadiaStandardIcon;
  fileTypes?: string[];
  maxSizeBytes?: number;
  isLoading?: boolean;
  isDisabled?: boolean;
  renderTrigger?(openFileBrowser: () => void): React.ReactNode;
  testID?: string;
}

/**
 * @deprecated use UploadRAC instead
 */
export function Upload(props: UploadProps) {
  const { t } = useTranslation();
  const id = useId();
  const {
    multiple = false,
    icon = "cloud_upload",
    fileTypes,
    maxSizeBytes = 10000000,
    isLoading,
    isDisabled = isLoading,
    testID = "upload",
  } = props;

  const { mediaQuery } = useMediaQuery();

  const [dragDropLayer, setDragDropLayer] = useState<number>(0);

  const handleDragEnter = () => {
    setDragDropLayer((layer) => layer + 1);
  };

  const handleDragLeave = () => {
    setDragDropLayer((layer) => layer - 1);
  };

  const handleDragOver = (e: React.DragEvent<HTMLButtonElement>) => {
    e.dataTransfer.dropEffect = "copy";
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    props.onFileSelected(e.dataTransfer.files);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      props.onFileSelected(e.target.files);
    }
  };

  const inputRef = createRef<HTMLInputElement>();

  if (props.renderTrigger) {
    return (
      <>
        <input
          ref={inputRef}
          disabled={isDisabled}
          multiple={multiple}
          hidden
          id={id}
          data-testid={`${testID}-file-input`}
          type="file"
          accept={fileTypes ? fileTypes.join(", ") : undefined}
          onChange={handleChange}
        />
        {props.renderTrigger(() => {
          inputRef.current?.click();
        })}
      </>
    );
  }

  return (
    <div data-testid={`${testID}-container`} style={styles.wrapper}>
      <button
        onClick={() => {
          inputRef.current?.click();
        }}
        onDragOver={handleDragOver}
        onDrop={(e) => {
          handleDrop(e);
          handleDragLeave();
        }}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        className={css(
          {
            "&:hover": css({
              backgroundColor: "var(--background-accent-neutral-weak)",
              "&:focus": css({
                outline: "0",
                boxShadow: "var(--focus-action)",
              }),
            }),
          },
          styles.dragDrop,
          styles.borders,
          styles.size,
          styles.colors,
          isDisabled && styles.disabled,
          dragDropLayer > 0 && {
            boxShadow: "var(--focus-action)",
          }
        )}
        data-testid={`${testID}-button`}
        type="button"
      >
        <div
          className={css(
            styles.align,
            styles.spacing,
            mediaQuery({
              paddingTop: ["var(--space-4)", "var(--space-5)"],
            })
          )}
        >
          <Icon type="standard" name={icon} color="icon-weak" size="space-6" />
        </div>
        <div className={css(styles.textAreaSpacing, styles.align)}>
          <div>
            <input
              ref={inputRef}
              disabled={isDisabled}
              multiple={multiple}
              hidden
              id={id}
              data-testid={`${testID}-file-input`}
              type="file"
              accept={fileTypes ? fileTypes.join(", ") : undefined}
              onChange={handleChange}
            />
            <label data-testid={`${testID}-label`}>
              <Text style="form/body-primary-link">{t("Browse files")}</Text>
              <Text
                style="form/body-primary"
                className={css(mediaQuery({ display: ["none", "inline"] }))}
              >
                {t(" or drag and drop to upload")}
              </Text>
              <Text
                style="form/body-primary"
                className={css(mediaQuery({ display: ["inline", "none"] }))}
              >
                {t(" to upload")}
              </Text>
            </label>
          </div>
        </div>
        <FileInputInfo fileTypes={fileTypes} maxSizeBytes={maxSizeBytes} />
      </button>
    </div>
  );
}

const styles = {
  wrapper: {
    display: "flex",
  },
  dragDrop: css({
    cursor: "pointer",
  }),
  colors: css({
    backgroundColor: "transparent",
  }),
  size: {
    minWidth: "fit-content",
    flexGrow: 1,
    paddingLeft: "var(--space-4)",
    paddingRight: "var(--space-4)",
    gap: "var(--space-3)",
    borderStyle: "dashed",
    borderWidth: "2px",
    borderRadius: "var(--border-radius-2)",
  },
  borders: {
    borderRadius: "4px",
  },
  spacing: {
    paddingBottom: "var(--space-3)",
  },
  textAreaSpacing: {
    paddingBottom: "var(--space-1-5)",
  },
  align: css({
    textAlign: "center",
  }),
  disabled: { opacity: 0.5 },
};
